import { BUILD_VERSION } from '@/generated/buildVersion'

const CHECK_INTERVAL_MS = 60 * 1000
const RELOAD_VERSION_KEY = 'time2lucky:build-reload-version'
let lastCheckedAt = 0
let pendingCheck: Promise<boolean> | null = null
let reloadStarted = false

function reloadWithVersion(version: string): boolean {
  if (typeof window === 'undefined') return false
  if (reloadStarted) return true
  const url = new URL(window.location.href)
  // A cached bundle or an inconsistent deployment must not keep reloading the tab.
  if (url.searchParams.get('_build') === version) return false
  try {
    if (window.sessionStorage.getItem(RELOAD_VERSION_KEY) === version) return false
    window.sessionStorage.setItem(RELOAD_VERSION_KEY, version)
  } catch (_) {
    // The URL marker still prevents a loop when browser storage is unavailable.
  }
  reloadStarted = true
  url.searchParams.set('_build', version)
  window.location.replace(url.toString())
  return true
}

export function ensureLatestBuild(force = false): Promise<boolean> {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return Promise.resolve(true)
  if (!force && Date.now() - lastCheckedAt < CHECK_INTERVAL_MS) return Promise.resolve(true)
  if (pendingCheck) return pendingCheck

  pendingCheck = window.fetch(`/build-version.json?t=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin'
  })
    .then(response => response.ok ? response.json() : null)
    .then(payload => {
      lastCheckedAt = Date.now()
      const remoteVersion = String(payload && payload.version || '').trim()
      if (remoteVersion && remoteVersion !== BUILD_VERSION) {
        return !reloadWithVersion(remoteVersion)
      }
      return true
    })
    .catch(error => {
      console.warn('Build version check failed:', error)
      return true
    })
    .finally(() => {
      pendingCheck = null
    })

  return pendingCheck
}

export function installBuildVersionGuard(): void {
  if (typeof window === 'undefined') return
  const check = () => {
    ensureLatestBuild(true)
  }
  window.addEventListener('focus', check)
  window.addEventListener('pageshow', check)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check()
  })
  window.setInterval(check, 5 * 60 * 1000)
}

